// src/components/product/AddProductModal.tsx
// Modal for creating new products in the catalog
// Uses hierarchical category dropdowns like AddItemModal

import React, { useState, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import { productCatalogAPI, Product, CreateProductRequest } from '../../services/productCatalogAPI';
import { CategoryHierarchy } from '../../types/category';
import { getItemTrackingConfig, getTrackingTypeLabel, getTrackingTypeDescription, requiresExpiration, getDefaultExpirationMonths } from '../../utils/categoryTracking';
import { tenantConfigAPI } from '../../services/tenantConfigAPI';

interface AddProductModalProps {
  onClose: () => void;
  onProductCreated: (product: Product) => void;
  categories: CategoryHierarchy[];
}

const AddProductModal: React.FC<AddProductModalProps> = ({ onClose, onProductCreated, categories }) => {
  const [formData, setFormData] = useState<CreateProductRequest & { trackingType?: string; standardExpirationMonths?: number }>({
    name: '',
    manufacturer: '',
    model: '',
    categoryId: '',
    subcategoryId: '',
    productType: 'SERIALIZED',
    trackingType: undefined,
    requiresSerialNumber: true,
    typicalUnitCost: undefined,
    preferredVendor: '',
    defaultLocation: '',
    minStockLevel: undefined,
    reorderPoint: undefined,
    unitOfMeasure: '',
    description: '',
    standardExpirationMonths: undefined
  });

  const [selectedParentCategory, setSelectedParentCategory] = useState<CategoryHierarchy | null>(null);
  const [availableSubcategories, setAvailableSubcategories] = useState<CategoryHierarchy[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Storage locations from tenant config
  const [storageLocations, setStorageLocations] = useState<string[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  // Check if selected category requires expiration tracking
  const selectedSubcategory = availableSubcategories.find(s => s.categoryId === formData.subcategoryId);
  const categoryRequiresExpiration = requiresExpiration(selectedSubcategory);

  // Load storage locations on mount
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const locations = await tenantConfigAPI.getInventoryLocations();
        setStorageLocations(locations);
      } catch (err) {
        console.error('Failed to load storage locations:', err);
      } finally {
        setLocationsLoading(false);
      }
    };
    loadLocations();
  }, []);

  // Build parent categories (top-level only)
  const parentCategories = categories.filter(c => !c.parentCategoryId);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value ? parseFloat(value) : undefined) : value
    }));
  };

  const handleProductTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const productType = e.target.value as 'SERIALIZED' | 'BULK';
    setFormData(prev => ({
      ...prev,
      productType,
      requiresSerialNumber: productType === 'SERIALIZED'
    }));
  };

  const handleParentCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const parentCategoryId = e.target.value;
    
    const parentCategory = categories.find(c => c.categoryId === parentCategoryId);
    setSelectedParentCategory(parentCategory || null);
    
    // Find subcategories
    const subcategories = categories.filter(c => c.parentCategoryId === parentCategoryId);
    setAvailableSubcategories(subcategories);
    
    // If no subcategories, use parent as the category
    if (subcategories.length === 0) {
      setFormData(prev => ({
        ...prev,
        categoryId: parentCategoryId,
        subcategoryId: undefined,
        standardExpirationMonths: undefined
      }));
    } else {
      // Clear category selection, force user to pick subcategory
      setFormData(prev => ({
        ...prev,
        categoryId: '',
        subcategoryId: undefined,
        standardExpirationMonths: undefined
      }));
    }
  };

  const handleSubcategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const subcategoryId = e.target.value;
    
    // Get the selected subcategory and its tracking config
    const selectedSubcat = availableSubcategories.find(s => s.categoryId === subcategoryId);
    const trackingConfig = getItemTrackingConfig(selectedSubcat);
    
    // Map trackingType to productType for backward compatibility
    const productType = trackingConfig.trackingType === 'SERIALIZED' ? 'SERIALIZED' : 'BULK';
    
    // Get default expiration months if category requires expiration
    const defaultExpMonths = getDefaultExpirationMonths(selectedSubcat);
    
    setFormData(prev => ({
      ...prev,
      categoryId: subcategoryId,
      subcategoryId: subcategoryId,
      parentCategoryId: selectedParentCategory?.categoryId,
      trackingType: trackingConfig.trackingType,
      productType: productType,
      requiresSerialNumber: trackingConfig.requiresSerialNumber,
      standardExpirationMonths: defaultExpMonths
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      setError('Product name is required');
      return;
    }
    
    if (!formData.categoryId) {
      setError('Category is required');
      return;
    }
    
    if (!formData.model?.trim()) {
      setError('Model is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Clean up empty strings to undefined
      const cleanedData: CreateProductRequest = {
        ...formData,
        manufacturer: formData.manufacturer?.trim() || undefined,
        model: formData.model?.trim() || undefined,
        subcategoryId: formData.subcategoryId?.trim() || undefined,
        preferredVendor: formData.preferredVendor?.trim() || undefined,
        defaultLocation: formData.defaultLocation?.trim() || undefined,
        unitOfMeasure: formData.unitOfMeasure?.trim() || undefined,
        description: formData.description?.trim() || undefined,
        standardExpirationMonths: formData.standardExpirationMonths || undefined
      };

      const newProduct = await productCatalogAPI.createProduct(cleanedData);
      
      console.log('✅ Product created successfully:', newProduct.productId);
      onProductCreated(newProduct);
      
    } catch (err: any) {
      console.error('Error creating product:', err);
      setError(err.message || 'Failed to create product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getSubcategoryState = () => {
    if (!selectedParentCategory) {
      return {
        disabled: true,
        placeholder: 'Select main category first',
        options: [],
        className: 'flex-1 px-3 py-2 border rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300'
      };
    }
    
    if (availableSubcategories.length === 0) {
      return {
        disabled: true,
        placeholder: 'No subcategories available',
        options: [],
        className: 'flex-1 px-3 py-2 border rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300'
      };
    }
    
    return {
      disabled: false,
      placeholder: 'Select subcategory...',
      options: availableSubcategories,
      className: 'flex-1 px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500 border-gray-300'
    };
  };

  const subcategoryState = getSubcategoryState();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">Add New Product</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
            disabled={loading}
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Product Information Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Product Information</h3>
            
            <div className="space-y-4">
              {/* Product Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Glock 19 Gen5"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Manufacturer and Model */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Manufacturer
                  </label>
                  <input
                    type="text"
                    name="manufacturer"
                    value={formData.manufacturer}
                    onChange={handleInputChange}
                    placeholder="e.g., Glock"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    placeholder="e.g., G19 Gen5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                
                <div className="space-y-2">
                  {/* Parent Category */}
                  <select 
                    value={selectedParentCategory?.categoryId || ''} 
                    onChange={handleParentCategoryChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select main category...</option>
                    {parentCategories.map(category => (
                      <option key={category.categoryId} value={category.categoryId}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  
                  {/* Subcategory */}
                  <div className="flex items-center space-x-2">
                    <ChevronRight className={`w-4 h-4 ${subcategoryState.disabled ? 'text-gray-300' : 'text-gray-400'}`} />
                    <select 
                      value={formData.subcategoryId || ''}
                      onChange={handleSubcategoryChange}
                      disabled={subcategoryState.disabled}
                      className={subcategoryState.className}
                    >
                      <option value="">{subcategoryState.placeholder}</option>
                      {subcategoryState.options.map(subcat => (
                        <option key={subcat.categoryId} value={subcat.categoryId}>
                          {subcat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Selection Display */}
                  {selectedParentCategory && (
                    <div className="text-xs text-gray-500 flex items-center space-x-1">
                      <span>Selected:</span>
                      <span className="font-medium">{selectedParentCategory.name}</span>
                      {formData.subcategoryId && (
                        <>
                          <ChevronRight className="w-3 h-3" />
                          <span className="font-medium">
                            {availableSubcategories.find(s => s.categoryId === formData.subcategoryId)?.name}
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Product Type - Auto-populated from subcategory */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tracking Type
                </label>
                {formData.trackingType ? (
                  <div className={`w-full px-3 py-2 border rounded-md ${
                    formData.trackingType === 'SERIALIZED' 
                      ? 'bg-blue-50 border-blue-200' 
                      : formData.trackingType === 'CONSUMABLE'
                        ? 'bg-orange-50 border-orange-200'
                        : 'bg-green-50 border-green-200'
                  }`}>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                      formData.trackingType === 'SERIALIZED' 
                        ? 'bg-blue-100 text-blue-800' 
                        : formData.trackingType === 'CONSUMABLE'
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-green-100 text-green-800'
                    }`}>
                      {formData.trackingType === 'SERIALIZED' ? 'Serialized' : 
                       formData.trackingType === 'CONSUMABLE' ? 'Consumable' : 'Bulk Tracked'}
                    </span>
                    <p className="text-xs text-gray-600 mt-2">
                      {formData.trackingType === 'SERIALIZED' 
                        ? 'Each item tracked individually with serial number'
                        : formData.trackingType === 'CONSUMABLE'
                          ? 'Tracked by quantity. Returns are disposed, not restocked.'
                          : 'Tracked by quantity. Returns restore inventory.'}
                    </p>
                  </div>
                ) : (
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
                    Select a subcategory to determine tracking type
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Tracking type is determined by the selected subcategory
                </p>
              </div>
            </div>
          </div>

          {/* Expiration Tracking Section - Only show if category requires expiration */}
          {categoryRequiresExpiration && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Expiration Tracking</h3>
              <p className="text-sm text-gray-600 mb-4">
                This category requires expiration tracking. Set the standard warranty/expiration period for this product.
              </p>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Standard Warranty/Expiration Period
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    name="standardExpirationMonths"
                    value={formData.standardExpirationMonths || ''}
                    onChange={handleInputChange}
                    min="1"
                    className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                    placeholder="60"
                  />
                  <span className="text-sm text-gray-600">months</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  e.g., 60 months (5 years) for body armor. Used to auto-calculate expiration when adding inventory items.
                </p>
              </div>
            </div>
          )}

          {/* Procurement Defaults Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Procurement Defaults</h3>
            
            <div className="space-y-4">
              {/* Typical Unit Cost and Preferred Vendor */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Typical Unit Cost
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">$</span>
                    <input
                      type="number"
                      name="typicalUnitCost"
                      value={formData.typicalUnitCost || ''}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Vendor
                  </label>
                  <input
                    type="text"
                    name="preferredVendor"
                    value={formData.preferredVendor}
                    onChange={handleInputChange}
                    placeholder="e.g., Glock Blue Label"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Default Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Location
                </label>
                <select
                  name="defaultLocation"
                  value={formData.defaultLocation}
                  onChange={handleInputChange}
                  disabled={locationsLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Select storage location...</option>
                  {storageLocations.map((loc) => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
                {locationsLoading && (
                  <p className="text-xs text-gray-500 mt-1">Loading locations...</p>
                )}
                {!locationsLoading && storageLocations.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No storage locations configured. Add them in Admin → Tenant Settings.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Inventory Rules Section (for BULK_TRACKED and CONSUMABLE items) */}
          {formData.trackingType && formData.trackingType !== 'SERIALIZED' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Inventory Rules (Bulk Items)</h3>
              
              <div className="space-y-4">
                {/* Min Stock Level, Reorder Point, Unit of Measure */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Min Stock Level
                    </label>
                    <input
                      type="number"
                      name="minStockLevel"
                      value={formData.minStockLevel || ''}
                      onChange={handleInputChange}
                      placeholder="1000"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reorder Point
                    </label>
                    <input
                      type="number"
                      name="reorderPoint"
                      value={formData.reorderPoint || ''}
                      onChange={handleInputChange}
                      placeholder="500"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unit of Measure
                    </label>
                    <input
                      type="text"
                      name="unitOfMeasure"
                      value={formData.unitOfMeasure}
                      onChange={handleInputChange}
                      placeholder="rounds, each, pairs"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Additional notes about this product..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Creating...
                </>
              ) : (
                'Create Product'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProductModal;